import { useEffect, useState, useRef } from 'react'
import {
  Plus,
  MessageSquare,
  Trash2,
  Loader2,
  History,
  PanelLeftClose,
  PanelLeft,
  X,
  Sparkles,
  Radio,
} from 'lucide-react'
import { useChatStore } from '@/store/chatStore'
import { useUIStore } from '@/store/uiStore'
import { useConnectionState } from '@/store/realtimeStore'

function formatDate(dateStr: string): string {
  const date = new Date(dateStr)
  const now = new Date()
  const diff = now.getTime() - date.getTime()
  const days = Math.floor(diff / (1000 * 60 * 60 * 24))

  if (days === 0) {
    return 'Today'
  } else if (days === 1) {
    return 'Yesterday'
  } else if (days < 7) {
    return `${days} days ago`
  } else {
    return date.toLocaleDateString()
  }
}

function truncateSummary(summary: string | undefined, maxLen = 50): string {
  if (!summary) return 'New conversation'
  if (summary.length <= maxLen) return summary
  return summary.substring(0, maxLen) + '...'
}

// Session list content - shared between mobile drawer and desktop sidebar
function SessionListContent({ onClose, hideHeader = false }: { onClose?: () => void; hideHeader?: boolean }) {
  const {
    sessions,
    sessionsLoading,
    sessionId: currentSessionId,
    loadSessions,
    loadSession,
    removeSession,
    newChat,
  } = useChatStore()

  // Real-time connection status
  const connectionState = useConnectionState()
  const isLive = connectionState === 'connected'

  // Track recently updated sessions for highlight animation
  const [recentlyUpdated, setRecentlyUpdated] = useState<Set<string>>(new Set())
  const prevSessionsRef = useRef(sessions)

  // Detect session updates and highlight them
  useEffect(() => {
    const prevSessions = prevSessionsRef.current
    const newUpdates = new Set<string>()

    sessions.forEach((session) => {
      const prevSession = prevSessions.find((s) => s.id === session.id)
      if (prevSession) {
        // Check if summary or message_count changed
        if (prevSession.summary !== session.summary || prevSession.message_count !== session.message_count) {
          newUpdates.add(session.id)
        }
      }
    })

    if (newUpdates.size > 0) {
      setRecentlyUpdated((prev) => new Set([...prev, ...newUpdates]))
      // Clear highlights after animation
      setTimeout(() => {
        setRecentlyUpdated((prev) => {
          const next = new Set(prev)
          newUpdates.forEach((id) => next.delete(id))
          return next
        })
      }, 2000)
    }

    prevSessionsRef.current = sessions
  }, [sessions])

  const handleNewChat = () => {
    newChat()
    onClose?.()
  }

  const handleLoadSession = (sessionId: string) => {
    loadSession(sessionId)
    onClose?.()
  }

  return (
    <>
      {/* Header - only show if not hidden */}
      {!hideHeader && (
        <div className="p-3 border-b border-surface-border">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold text-text flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-accent" />
              Conversations
              {/* Live indicator */}
              {isLive && (
                <span
                  className="flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-green-500/10 text-green-500 text-[10px] font-medium"
                  title="Real-time updates enabled"
                >
                  <Radio className="w-2.5 h-2.5 animate-pulse" />
                  Live
                </span>
              )}
            </h2>
            {onClose && (
              <button
                onClick={onClose}
                className="p-1.5 rounded-lg hover:bg-surface-hover text-text-muted hover:text-text transition-colors md:hidden"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          <button
            onClick={handleNewChat}
            className="w-full flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl bg-gradient-to-r from-accent to-accent-hover text-white text-sm font-medium hover:opacity-90 transition-all active:scale-[0.98]"
          >
            <Plus className="w-4 h-4" />
            New Chat
          </button>
        </div>
      )}

      {/* Session list */}
      <div className="flex-1 overflow-y-auto p-2 space-y-1">
        {sessionsLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-5 h-5 animate-spin text-accent" />
          </div>
        ) : sessions.length === 0 ? (
          <div className="text-center py-12 text-text-muted text-sm">
            <div className="w-12 h-12 rounded-xl bg-surface-hover flex items-center justify-center mx-auto mb-3">
              <MessageSquare className="w-6 h-6 opacity-50" />
            </div>
            <p className="font-medium">No conversations yet</p>
            <p className="text-xs mt-1 text-text-secondary">Start a new chat to begin</p>
          </div>
        ) : (
          sessions.map((session, index) => (
            <div
              key={session.id}
              className={`group relative flex items-start gap-2.5 p-3 rounded-xl cursor-pointer transition-all hover:scale-[1.01] active:scale-[0.99] animate-in fade-in ${
                currentSessionId === session.id
                  ? 'bg-accent/10 border border-accent/20'
                  : 'hover:bg-surface-hover border border-transparent'
              } ${
                recentlyUpdated.has(session.id)
                  ? 'ring-2 ring-green-500/50 ring-offset-1 ring-offset-surface'
                  : ''
              }`}
              style={{ animationDelay: `${index * 30}ms` }}
              onClick={() => handleLoadSession(session.id)}
            >
              <div
                className={`p-2 rounded-lg ${
                  currentSessionId === session.id ? 'bg-accent/20' : 'bg-surface-hover'
                }`}
              >
                <MessageSquare
                  className={`w-4 h-4 ${
                    currentSessionId === session.id ? 'text-accent' : 'text-text-muted'
                  }`}
                />
              </div>
              <div className="flex-1 min-w-0">
                <div
                  className={`text-sm font-medium truncate ${
                    currentSessionId === session.id ? 'text-accent' : 'text-text'
                  }`}
                >
                  {truncateSummary(session.summary)}
                </div>
                <div className="flex items-center gap-2 text-xs text-text-muted mt-1">
                  <span>{formatDate(session.updated_at)}</span>
                  <span className="w-1 h-1 rounded-full bg-text-muted/50" />
                  <span>{session.message_count} msgs</span>
                </div>
              </div>
              {/* Delete button - appears on hover */}
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  removeSession(session.id)
                }}
                className="absolute right-2 top-2 p-1.5 rounded-lg opacity-0 group-hover:opacity-100 hover:bg-red-500/10 text-text-muted hover:text-red-400 transition-all"
                title="Delete conversation"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))
        )}
      </div>

      {/* Footer */}
      <div className="p-3 border-t border-surface-border">
        <button
          onClick={() => loadSessions()}
          className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-xl text-xs text-text-muted hover:bg-surface-hover hover:text-text transition-all"
        >
          <History className="w-3.5 h-3.5" />
          Refresh
        </button>
      </div>
    </>
  )
}

export default function SessionList() {
  const { sessionSidebarOpen, setSessionSidebarOpen } = useChatStore()
  const { mobileSessionsOpen, setMobileSessionsOpen } = useUIStore()
  const { loadSessions, newChat } = useChatStore()
  const connectionState = useConnectionState()

  useEffect(() => {
    loadSessions()
  }, [loadSessions])

  return (
    <>
      {/* Mobile drawer overlay */}
      {mobileSessionsOpen && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 md:hidden animate-in fade-in duration-200"
          onClick={() => setMobileSessionsOpen(false)}
        />
      )}

      {/* Mobile drawer */}
      <aside
        className={`
          fixed inset-y-0 left-0 z-50 w-72 bg-surface border-r border-surface-border flex flex-col
          transform transition-transform duration-300 ease-out md:hidden
          ${mobileSessionsOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
      >
        <SessionListContent onClose={() => setMobileSessionsOpen(false)} />
      </aside>

      {/* Desktop: Collapsed sidebar */}
      {!sessionSidebarOpen && (
        <div className="hidden md:flex w-14 bg-surface border-r border-surface-border flex-col items-center py-4 space-y-3">
          <button
            onClick={() => setSessionSidebarOpen(true)}
            className="p-2.5 rounded-xl hover:bg-surface-hover text-text-muted hover:text-text transition-all hover:scale-105"
            title="Expand sidebar"
          >
            <PanelLeft className="w-5 h-5" />
          </button>
          <button
            onClick={newChat}
            className="p-2.5 rounded-xl bg-accent/10 text-accent hover:bg-accent/20 transition-all hover:scale-105"
            title="New chat"
          >
            <Plus className="w-5 h-5" />
          </button>
          <div className="flex-1" />
          <button
            onClick={() => loadSessions()}
            className="p-2.5 rounded-xl hover:bg-surface-hover text-text-muted hover:text-text transition-all hover:scale-105"
            title="Refresh sessions"
          >
            <History className="w-5 h-5" />
          </button>
        </div>
      )}

      {/* Desktop: Expanded sidebar */}
      {sessionSidebarOpen && (
        <div className="hidden md:flex w-72 bg-surface border-r border-surface-border flex-col">
          <div className="p-3 border-b border-surface-border">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-semibold text-text flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-accent" />
                Conversations
                {/* Live indicator */}
                {connectionState === 'connected' && (
                  <span
                    className="flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-green-500/10 text-green-500 text-[10px] font-medium"
                    title="Real-time updates enabled"
                  >
                    <Radio className="w-2.5 h-2.5 animate-pulse" />
                    Live
                  </span>
                )}
              </h2>
              <button
                onClick={() => setSessionSidebarOpen(false)}
                className="p-1.5 rounded-lg hover:bg-surface-hover text-text-muted hover:text-text transition-colors"
                title="Collapse sidebar"
              >
                <PanelLeftClose className="w-4 h-4" />
              </button>
            </div>
            <button
              onClick={newChat}
              className="w-full flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl bg-gradient-to-r from-accent to-accent-hover text-white text-sm font-medium hover:opacity-90 transition-all active:scale-[0.98]"
            >
              <Plus className="w-4 h-4" />
              New Chat
            </button>
          </div>
          <SessionListContent hideHeader={true} />
        </div>
      )}
    </>
  )
}
